import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useProject } from '../context/ProjectContext';
import { useChartPreferences } from '../context/ChartPreferencesContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Plus, Trash2, Camera, Save, ChevronLeft, ChevronRight, Download, CheckSquare, Square, FileSpreadsheet, TrendingUp } from 'lucide-react';
import { Checkbox } from './ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { formatCurrency, formatPercent, cn } from '../lib/utils';
import { exportInvestmentsToExcel } from './ExportExcelButton';
import { FormattedNumberInput } from './FormattedNumberInput';
import { UnsavedChangesDialog, useUnsavedBlocker, BlockerDialog } from './UnsavedChangesDialog';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, LabelList
} from 'recharts';

const COLORS = ['#3B82F6', '#0EA5E9', '#10B981', '#F59E0B', '#8B5CF6', '#EC4899', '#6366F1'];

export const InvestmentPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { currentProject, fetchProject, updateProject, uploadImage, getCalculation } = useProject();
  const { showLabels, formatChartValue } = useChartPreferences();
  const fileInputRefs = useRef({});
  
  const [investments, setInvestments] = useState([]);
  const [depreciation, setDepreciation] = useState(null);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('inputs');
  const [initialInvestments, setInitialInvestments] = useState(null);
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const [revalDialogOpen, setRevalDialogOpen] = useState(false);
  const [revalItemId, setRevalItemId] = useState(null);
  const [revalRate, setRevalRate] = useState(0);

  useEffect(() => {
    const loadData = async () => {
      if (!currentProject || currentProject.id !== projectId) {
        await fetchProject(projectId);
      }
    };
    loadData();
  }, [projectId, currentProject, fetchProject]);

  useEffect(() => {
    if (currentProject?.investments) {
      const invData = currentProject.investments.length > 0 ? currentProject.investments.map(i => ({
        ...i,
        include_salvage: i.include_salvage !== false,
        has_revaluation: i.has_revaluation || false,
        revaluation_rate: i.revaluation_rate || 0,
        salvage_type: i.salvage_type || 'book_value',
        salvage_manual_value: i.salvage_manual_value || 0
      })) : [
        { id: crypto.randomUUID(), concept: '', amount: 0, depreciation_years: 5, photo: null, include_salvage: true, has_revaluation: false, revaluation_rate: 0, salvage_type: 'book_value', salvage_manual_value: 0 }
      ];
      setInvestments(invData);
      setInitialInvestments(JSON.stringify(invData));
    }
  }, [currentProject]);

  useEffect(() => {
    const loadDepreciation = async () => {
      if (activeTab === 'summary' && investments.some(i => i.amount > 0)) {
        try {
          const data = await getCalculation(projectId, 'depreciation');
          setDepreciation(data);
        } catch (err) {
          console.error('Error loading depreciation:', err);
        }
      }
    };
    loadDepreciation();
  }, [activeTab, investments, projectId, getCalculation]);

  const addRow = () => {
    setInvestments(prev => [...prev, {
      id: crypto.randomUUID(),
      concept: '',
      amount: 0,
      depreciation_years: 5,
      photo: null,
      include_salvage: true,
      has_revaluation: false,
      revaluation_rate: 0,
      salvage_type: 'book_value',
      salvage_manual_value: 0
    }]);
  };

  const removeRow = (id) => {
    if (investments.length === 1) {
      toast.error('Debe haber al menos un concepto');
      return;
    }
    setInvestments(prev => prev.filter(i => i.id !== id));
  };

  const updateRow = (id, field, value) => {
    setInvestments(prev => prev.map(i => 
      i.id === id ? { ...i, [field]: value } : i
    ));
  };

  const handlePhotoUpload = async (id, e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const imageData = await uploadImage(file);
      updateRow(id, 'photo', imageData);
      toast.success('Imagen cargada');
    } catch (err) {
      toast.error('Error al cargar imagen');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const validInvestments = investments.filter(i => i.concept.trim());
      const totalInvestment = validInvestments.reduce((sum, i) => sum + (parseFloat(i.amount) || 0), 0);
      
      await updateProject(projectId, {
        investments: validInvestments,
        financing: {
          ...currentProject.financing,
          total_investment: totalInvestment,
          equity_amount: totalInvestment * ((currentProject.financing?.equity_percentage || 100) / 100),
          debt_amount: totalInvestment * ((currentProject.financing?.debt_percentage || 0) / 100)
        }
      });
      setInitialInvestments(JSON.stringify(investments));
      toast.success('Inversiones guardadas');
    } catch (err) {
      if (err.response?.status !== 403) toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const hasUnsavedChanges = initialInvestments && JSON.stringify(investments) !== initialInvestments;

  const openRevalDialog = (id) => {
    const item = investments.find(i => i.id === id);
    setRevalItemId(id);
    setRevalRate(item?.revaluation_rate || 0);
    setRevalDialogOpen(true);
  };

  const confirmRevaluation = () => {
    setInvestments(prev => prev.map(i =>
      i.id === revalItemId
        ? { ...i, has_revaluation: true, revaluation_rate: parseFloat(revalRate) || 0, depreciation_years: 0 }
        : i
    ));
    setRevalDialogOpen(false);
    setRevalItemId(null);
  };

  const removeRevaluation = (id) => {
    setInvestments(prev => prev.map(i =>
      i.id === id
        ? { ...i, has_revaluation: false, revaluation_rate: 0, depreciation_years: 5 }
        : i
    ));
  };

  const { showDialog, tryNavigate, handleSaveAndProceed, handleDiscard, handleCancel } = useUnsavedBlocker(hasUnsavedChanges, handleSave);

  const goToNext = () => {
    if (!tryNavigate(`/app/project/${projectId}/financing`)) {
      navigate(`/app/project/${projectId}/financing`);
    }
  };

  const goToPrev = () => {
    if (!tryNavigate(`/app/project/${projectId}/specifications`)) {
      navigate(`/app/project/${projectId}/specifications`);
    }
  };

  const totalInvestment = investments.reduce((sum, i) => sum + (parseFloat(i.amount) || 0), 0);
  
  // Calcular depreciación promedio basada en años
  const avgDepreciationYears = investments.filter(i => i.depreciation_years > 0).length > 0 
    ? investments.reduce((sum, i) => sum + (parseFloat(i.depreciation_years) || 5), 0) / investments.filter(i => i.depreciation_years > 0).length
    : 5;

  const chartData = investments
    .filter(i => i.amount > 0)
    .sort((a, b) => b.amount - a.amount)
    .map((i, idx) => ({
      name: i.concept || 'Sin nombre',
      amount: i.amount,
      photo: i.photo,
      color: COLORS[idx % COLORS.length]
    }));

  return (
    <div className="space-y-6 animate-fade-in" data-testid="investment-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
            <span className="px-2 py-1 bg-primary/20 text-primary rounded text-xs font-medium">PASO 2 DE 10</span>
          </div>
          <h1 className="text-4xl font-bold">Inversión Necesaria</h1>
          <p className="text-muted-foreground mt-1">
            Gastos de capital para activos fijos y operaciones iniciales.
          </p>
        </div>
        <div className="flex gap-3">
          <Button 
            variant="outline" 
            className="gap-2"
            onClick={() => exportInvestmentsToExcel(investments, currentProject?.name || 'Proyecto', currentProject?.specifications?.currency || 'MXN')}
          >
            <FileSpreadsheet className="w-4 h-4" />
            Excel
          </Button>
          <Button onClick={addRow} className="gap-2" data-testid="add-row-btn">
            <Plus className="w-4 h-4" />
            Agregar Fila
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-secondary/50">
          <TabsTrigger value="inputs" data-testid="inputs-tab">
            <Plus className="w-4 h-4 mr-2" />
            INPUTS
          </TabsTrigger>
          <TabsTrigger value="summary" data-testid="summary-tab">
            <BarChart className="w-4 h-4 mr-2" />
            RESUMEN
          </TabsTrigger>
        </TabsList>

        <TabsContent value="inputs" className="mt-6">
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-secondary/30">
                    <TableHead className="w-12">#</TableHead>
                    <TableHead>CONCEPTO</TableHead>
                    <TableHead className="text-right">MONTO ({currentProject?.specifications?.currency || 'MXN'})</TableHead>
                    <TableHead className="text-center w-24">FOTO</TableHead>
                    <TableHead className="text-center">VIDA ÚTIL (AÑOS)</TableHead>
                    <TableHead className="text-center">% ANUAL</TableHead>
                    <TableHead className="text-center w-28">VALOR RESCATE</TableHead>
                    <TableHead className="text-center w-32">REVALUACIÓN</TableHead>
                    <TableHead className="w-12"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {investments.map((item, idx) => (
                    <TableRow key={item.id} className="group">
                      <TableCell className="font-medium text-muted-foreground">
                        {String(idx + 1).padStart(2, '0')}
                      </TableCell>
                      <TableCell>
                        <Input
                          placeholder="Ej: Maquinaria Industrial"
                          value={item.concept}
                          onChange={(e) => updateRow(item.id, 'concept', e.target.value)}
                          className="bg-transparent border-0 focus-visible:ring-1"
                          data-testid={`concept-input-${idx}`}
                        />
                      </TableCell>
                      <TableCell className="text-right">
                        <FormattedNumberInput
                          placeholder="0"
                          value={item.amount}
                          onChange={(val) => updateRow(item.id, 'amount', val)}
                          className="bg-transparent border-0 text-right tabular-nums focus-visible:ring-1"
                          data-testid={`amount-input-${idx}`}
                        />
                      </TableCell>
                      <TableCell className="text-center">
                        <button
                          onClick={() => fileInputRefs.current[item.id]?.click()}
                          className="w-10 h-10 rounded-lg bg-secondary flex items-center justify-center mx-auto hover:bg-secondary/80 transition-colors overflow-hidden"
                        >
                          {item.photo ? (
                            <img src={item.photo} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <Camera className="w-4 h-4 text-muted-foreground" />
                          )}
                        </button>
                        <input
                          ref={(el) => fileInputRefs.current[item.id] = el}
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => handlePhotoUpload(item.id, e)}
                        />
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Input
                            type="number"
                            placeholder="5"
                            min="0"
                            max="50"
                            value={item.depreciation_years === 0 ? '0' : (item.depreciation_years || '')}
                            onChange={(e) => {
                              const val = e.target.value;
                              updateRow(item.id, 'depreciation_years', val === '' ? 0 : parseInt(val));
                            }}
                            className="w-16 bg-transparent border-0 text-center tabular-nums focus-visible:ring-1"
                            data-testid={`depreciation-input-${idx}`}
                            disabled={item.has_revaluation}
                          />
                          <span className="text-muted-foreground text-sm">años</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-center tabular-nums text-muted-foreground">
                        {item.has_revaluation ? (
                          <span className="text-xs text-success">+{item.revaluation_rate}%/año</span>
                        ) : item.depreciation_years > 0 ? formatPercent(100 / item.depreciation_years) : '--'}
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex flex-col items-center gap-1">
                          <Checkbox
                            checked={item.include_salvage !== false}
                            onCheckedChange={(checked) => {
                              updateRow(item.id, 'include_salvage', checked);
                              if (!checked) {
                                removeRevaluation(item.id);
                              }
                            }}
                            className="data-[state=checked]:bg-success data-[state=checked]:border-success"
                            data-testid={`salvage-checkbox-${idx}`}
                          />
                          {item.include_salvage !== false && !item.has_revaluation && (
                            <div className="flex items-center gap-1">
                              <Button type="button" size="sm" variant={item.salvage_type !== 'manual' ? 'default' : 'outline'}
                                className="h-5 text-[10px] px-1.5" onClick={() => updateRow(item.id, 'salvage_type', 'book_value')}>
                                Libros
                              </Button>
                              <Button type="button" size="sm" variant={item.salvage_type === 'manual' ? 'default' : 'outline'}
                                className="h-5 text-[10px] px-1.5" onClick={() => updateRow(item.id, 'salvage_type', 'manual')}
                                data-testid={`salvage-manual-btn-${idx}`}>
                                Manual
                              </Button>
                            </div>
                          )}
                          {item.include_salvage !== false && item.salvage_type === 'manual' && !item.has_revaluation && (
                            <Input type="number" className="h-6 text-xs text-right w-24"
                              value={item.salvage_manual_value || ''}
                              placeholder="0"
                              onChange={(e) => updateRow(item.id, 'salvage_manual_value', parseFloat(e.target.value) || 0)}
                              data-testid={`salvage-manual-value-${idx}`}
                            />
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        {item.include_salvage !== false ? (
                          item.has_revaluation ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openRevalDialog(item.id)}
                              className="text-xs text-success hover:text-success gap-1 px-2 h-7"
                              data-testid={`reval-edit-btn-${idx}`}
                            >
                              <TrendingUp className="w-3 h-3" />
                              {item.revaluation_rate}%
                            </Button>
                          ) : (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openRevalDialog(item.id)}
                              className="text-xs text-muted-foreground hover:text-primary gap-1 px-2 h-7"
                              data-testid={`reval-btn-${idx}`}
                            >
                              <TrendingUp className="w-3 h-3" />
                              Revalúa
                            </Button>
                          )
                        ) : (
                          <span className="text-xs text-muted-foreground">--</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => removeRow(item.id)}
                          className="opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Summary Row */}
              <div className="flex items-center justify-between p-4 bg-secondary/30 border-t border-border">
                <div className="flex items-center gap-8">
                  <div>
                    <p className="text-xs text-muted-foreground uppercase">Total Conceptos</p>
                    <p className="text-xl font-bold">{investments.filter(i => i.concept).length.toString().padStart(2, '0')} Items</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase">Vida Útil Promedio</p>
                    <p className="text-xl font-bold">{avgDepreciationYears.toFixed(1)} años</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-xs text-success uppercase">Inversión Total</p>
                  <p className="text-3xl font-bold text-success">
                    {formatCurrency(totalInvestment, currentProject?.specifications?.currency || 'MXN')}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <p className="text-sm text-muted-foreground mt-2">
            💡 Presiona Enter para agregar una nueva fila. Todos los datos se sincronizan automáticamente.
          </p>
        </TabsContent>

        <TabsContent value="summary" className="mt-6 space-y-6">
          {/* Investment Breakdown Chart */}
          <Card>
            <CardHeader>
              <CardTitle>Desglose de Inversión</CardTitle>
            </CardHeader>
            <CardContent>
              {chartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={chartData} layout="vertical" margin={{ left: 20, right: 100 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#2D3142" horizontal={false} />
                    <XAxis type="number" stroke="#94A3B8" tickFormatter={(v) => formatChartValue(v)} />
                    <YAxis type="category" dataKey="name" stroke="#94A3B8" width={150} fontSize={12} />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#13151F', border: '1px solid #2D3142' }}
                      formatter={(value) => formatCurrency(value)}
                    />
                    <Bar dataKey="amount" radius={[0, 4, 4, 0]}>
                      {chartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                      {showLabels && (
                        <LabelList 
                          dataKey="amount" 
                          position="right" 
                          formatter={(v) => formatChartValue(v)} 
                          fill="#F8FAFC"
                          fontSize={12}
                        />
                      )}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                  Agrega conceptos de inversión para ver el desglose
                </div>
              )}
            </CardContent>
          </Card>

          {/* Depreciation Chart */}
          <Card>
            <CardHeader>
              <CardTitle>Depreciación Anual y Valor de Rescate</CardTitle>
            </CardHeader>
            <CardContent>
              {depreciation && depreciation.annual_depreciation?.length > 0 ? (
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <h4 className="text-sm font-medium text-muted-foreground mb-4">Depreciación por Año</h4>
                    <ResponsiveContainer width="100%" height={200}>
                      <BarChart data={depreciation.annual_depreciation}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#2D3142" />
                        <XAxis dataKey="year" stroke="#94A3B8" tickFormatter={(v) => `Año ${v}`} fontSize={12} />
                        <YAxis stroke="#94A3B8" tickFormatter={(v) => formatChartValue(v)} fontSize={12} />
                        <Tooltip 
                          contentStyle={{ backgroundColor: '#13151F', border: '1px solid #2D3142' }}
                          formatter={(value) => formatCurrency(value)}
                        />
                        <Bar dataKey="amount" fill="#F59E0B" radius={[4, 4, 0, 0]} name="Depreciación">
                          {showLabels && (
                            <LabelList dataKey="amount" position="top" formatter={(v) => formatChartValue(v)} fill="#94A3B8" fontSize={10} />
                          )}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-muted-foreground mb-4">Valor de Rescate</h4>
                    <ResponsiveContainer width="100%" height={200}>
                      <BarChart data={depreciation.salvage_values}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#2D3142" />
                        <XAxis dataKey="year" stroke="#94A3B8" tickFormatter={(v) => `Año ${v}`} fontSize={12} />
                        <YAxis stroke="#94A3B8" tickFormatter={(v) => formatChartValue(v)} fontSize={12} />
                        <Tooltip 
                          contentStyle={{ backgroundColor: '#13151F', border: '1px solid #2D3142' }}
                          formatter={(value) => formatCurrency(value)}
                        />
                        <Bar dataKey="amount" fill="#10B981" radius={[4, 4, 0, 0]} name="Valor Residual">
                          {showLabels && (
                            <LabelList dataKey="amount" position="top" formatter={(v) => formatChartValue(v)} fill="#94A3B8" fontSize={10} />
                          )}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              ) : (
                <div className="h-[200px] flex items-center justify-center text-muted-foreground">
                  Guarda los datos para ver las proyecciones de depreciación
                </div>
              )}

              {depreciation && (
                <div className="mt-6 p-4 bg-secondary/30 rounded-lg flex items-center justify-between">
                  <span className="text-muted-foreground">Valor de Rescate Final (Año {currentProject?.specifications?.project_life_years || 5})</span>
                  <span className="text-2xl font-bold text-success">
                    {formatCurrency(depreciation.final_salvage || 0, currentProject?.specifications?.currency || 'MXN')}
                  </span>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Actions */}
      <div className="flex justify-between">
        <Button variant="outline" onClick={goToPrev}>
          <ChevronLeft className="w-4 h-4 mr-2" />
          Anterior
        </Button>
        <div className="flex gap-3">
          <Button variant="secondary" onClick={handleSave} disabled={saving}>
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Guardando...' : 'Guardar'}
          </Button>
          <Button onClick={goToNext} data-testid="continue-btn">
            Continuar
            <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>

      {/* Unsaved Changes Dialog */}
      <BlockerDialog showDialog={showDialog} onSave={handleSaveAndProceed} onDiscard={handleDiscard} onCancel={handleCancel} saving={saving} />

      {/* Revaluation Rate Dialog */}
      <Dialog open={revalDialogOpen} onOpenChange={setRevalDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-success" />
              Tasa de Revaluación Anual
            </DialogTitle>
            <DialogDescription>
              Define la tasa anual a la que se revalúa este activo. La depreciación será cero y el valor de rescate se calculará con apreciación compuesta.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="reval-rate" className="text-sm font-medium">
                Tasa de revaluación anual (%)
              </Label>
              <div className="flex items-center gap-2">
                <Input
                  id="reval-rate"
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={revalRate}
                  onChange={(e) => setRevalRate(e.target.value)}
                  className="text-lg"
                  data-testid="reval-rate-input"
                  autoFocus
                />
                <span className="text-muted-foreground font-medium">%</span>
              </div>
              <p className="text-xs text-muted-foreground">
                Ej: Un terreno que se revalúa al 5% anual sobre un monto de {formatCurrency(investments.find(i => i.id === revalItemId)?.amount || 0, currentProject?.specifications?.currency || 'MXN')} valdrá {formatCurrency((investments.find(i => i.id === revalItemId)?.amount || 0) * Math.pow(1 + (parseFloat(revalRate) || 0) / 100, currentProject?.specifications?.project_life_years || 5), currentProject?.specifications?.currency || 'MXN')} al final del proyecto.
              </p>
            </div>
          </div>
          <DialogFooter className="gap-2">
            {investments.find(i => i.id === revalItemId)?.has_revaluation && (
              <Button
                variant="outline"
                className="text-destructive hover:text-destructive"
                onClick={() => {
                  removeRevaluation(revalItemId);
                  setRevalDialogOpen(false);
                }}
                data-testid="reval-remove-btn"
              >
                Quitar Revaluación
              </Button>
            )}
            <Button variant="outline" onClick={() => setRevalDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={confirmRevaluation} data-testid="reval-confirm-btn">
              Confirmar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
